import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { STALE_TIME } from '@/api/staleTimes';
import { downloadApiFile } from '@/utils/download';
import {
  pcmpMoReportSchema,
  type PcmpMoReport,
  type PcmpMoReportFilters,
} from '../types/pcmpMoReport.types';
import { pcmpReportQueryKeys } from './pcmpReports.api';

/** The API takes the project list as `projectIds=1,2`, not a repeated key. */
function toMoReportParams({ projectIds, ...rest }: PcmpMoReportFilters) {
  return { ...rest, projectIds: projectIds?.length ? projectIds.join(',') : undefined };
}

export const pcmpMoReportQueryOptions = (filters: PcmpMoReportFilters) =>
  queryOptions({
    queryKey: [...pcmpReportQueryKeys.all(), 'mo-wise', filters] as const,
    queryFn: async (): Promise<PcmpMoReport> => {
      const res = await apiClient.get(API.pcmpReport.moWise, { params: toMoReportParams(filters) });
      return pcmpMoReportSchema.parse(res.data);
    },
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

/**
 * The API names the file `PCMP_MO_Report_<HR id | scope>_<date>.xlsx`; its header
 * is not readable cross-origin, so the same name is built here.
 */
export function pcmpMoReportXlsxFilename(
  report: Pick<PcmpMoReport, 'scope' | 'mo' | 'reportDate'>,
): string {
  const who = report.scope === 'MO' && report.mo?.hrId ? report.mo.hrId : report.scope;
  return `PCMP_MO_Report_${who.replace(/[^A-Za-z0-9_-]/g, '')}_${report.reportDate}.xlsx`;
}

export function downloadPcmpMoReportXlsx(
  filters: PcmpMoReportFilters,
  report: Pick<PcmpMoReport, 'scope' | 'mo' | 'reportDate'>,
): Promise<void> {
  return downloadApiFile(
    API.pcmpReport.moWiseXlsx,
    pcmpMoReportXlsxFilename(report),
    toMoReportParams(filters),
  );
}
